import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, Undo2, GitCompareArrows, ChevronDown, ChevronUp } from "lucide-react";

const BADGE = { resolved: ["Needs review", "text-amber-300 border-amber-500/40"], confirmed: ["Confirmed", "text-emerald-400 border-emerald-500/40"], rejected: ["Reopened", "text-slate-400 border-slate-600"], unmatched: ["No match", "text-slate-500 border-slate-700"] };

function PriorFinding({ it }) {
  const p = it.prior_finding;
  if (!p) return <div className="text-slate-500 italic">“{it.addendum_finding.supersedes_finding}” — no open finding matched; nothing was changed</div>;
  return (
    <>
      <div className={`font-medium ${it.state === "rejected" ? "text-slate-300" : "text-slate-400 line-through decoration-slate-600"}`}>{p.title}</div>
      <div className="text-slate-500 font-mono">{p.csi_division} · p.{p.source_page} · now {p.status.replace(/_/g, " ")}</div>
    </>
  );
}

function QueueItem({ it, canEdit, act }) {
  const a = it.addendum_finding, p = it.prior_finding, [label, cls] = BADGE[it.state];
  return (
    <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto] gap-3 items-center text-xs" data-testid={`supersede-item-${a.id}`}>
      <div className="min-w-0">
        <div className="label-cap text-slate-500">Addendum says</div>
        <div className="text-slate-100 font-medium">{a.title}</div>
        <div className="text-slate-500 font-mono">p.{a.source_page} · {String(a.change_type || "").replace(/_/g, " ")} · cost {a.cost_impact || "unknown"}</div>
      </div>
      <div className="hidden md:block text-slate-600">⇄</div>
      <div className="min-w-0"><div className="label-cap text-slate-500">Earlier finding</div><PriorFinding it={it} /></div>
      <div className="flex items-center gap-2 justify-end">
        <span className={`border rounded px-1.5 py-0.5 font-mono text-[10px] uppercase ${cls}`}>{label}</span>
        {canEdit && it.state === "resolved" && (<>
          <button onClick={() => act(`/findings/${a.id}/confirm-supersede`, "Match confirmed")} className="inline-flex items-center gap-1 text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/10 rounded px-2 py-1" data-testid={`confirm-supersede-${a.id}`}><CheckCircle2 className="w-3.5 h-3.5" /> Confirm</button>
          <button onClick={() => act(`/findings/${p.id}/reopen`, "Earlier finding reopened")} className="inline-flex items-center gap-1 text-amber-300 border border-amber-500/40 hover:bg-amber-500/10 rounded px-2 py-1" data-testid={`queue-reopen-${p.id}`}><Undo2 className="w-3.5 h-3.5" /> Reopen</button>
        </>)}
      </div>
    </div>
  );
}

export function SupersedeQueue({ projectId, canEdit, onChanged }) {
  const [q, setQ] = useState(null);
  const [open, setOpen] = useState(true);
  const load = useCallback(() => api.get(`/projects/${projectId}/supersede-queue`).then((r) => setQ(r.data)).catch(() => setQ(null)), [projectId]);
  useEffect(() => { load(); }, [load]);
  if (!q?.run || !q.items.length) return null;
  const act = async (url, msg) => { try { await api.post(url); toast.success(msg); await load(); onChanged?.(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  const confirmAll = async () => { for (const it of q.items.filter((i) => i.state === "resolved")) await api.post(`/findings/${it.addendum_finding.id}/confirm-supersede`); toast.success("All confirmed"); await load(); onChanged?.(); };
  const matched = q.items.filter((i) => i.prior_finding).length;
  return (
    <div className="sc-card border-amber-500/30 mb-4" data-testid="supersede-queue">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left">
        <div className="flex items-center gap-2 min-w-0">
          <GitCompareArrows className="w-4 h-4 text-amber-400 shrink-0" />
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-100 truncate">Addendum supersede review · {q.pending} to confirm</div>
            <div className="text-xs text-slate-500 truncate">Latest addendum run {new Date(q.run.created_at).toLocaleDateString()} auto-resolved {matched} earlier finding{matched === 1 ? "" : "s"} — confirm each match or reopen if wrong.</div>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {canEdit && q.pending > 0 && <span role="button" onClick={(e) => { e.stopPropagation(); confirmAll(); }} className="text-xs text-emerald-400 border border-emerald-500/40 hover:bg-emerald-500/10 rounded px-2 py-1" data-testid="confirm-all-supersedes">Confirm all</span>}
          {open ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </div>
      </button>
      {open && <div className="border-t border-slate-800 divide-y divide-slate-800/70">{q.items.map((it) => <QueueItem key={it.addendum_finding.id} it={it} canEdit={canEdit} act={act} />)}</div>}
    </div>
  );
}
